/**
 * Compiles the vendored OCPP 2.0.1 JSON Schemas (draft-06 per upstream
 * `$schema` declaration) with Ajv at module load and exposes per-action
 * validators.
 *
 * Session 10a registers only Boot / Heartbeat / StatusNotification — the
 * remaining 60+ action schemas are vendored on disk but not compiled
 * until the action lands in a later 10x session. `validatorFor()` returns
 * a sentinel `{ valid: false, errors: ['unknown action …'] }` for
 * unregistered actions, which #send in OcppV201Adapter catches before
 * the wire-out path.
 *
 * Why a fresh Ajv (not the v16 instance):
 *   v1.6 schemas are draft-04, vendored under `ocpp16/`; v2.0.1 schemas
 *   are draft-06, vendored under `ocpp201/`. Sharing one Ajv would force
 *   one metaschema profile on both and surface phantom validation drift.
 */
import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import { relaxOcppUriFormats } from '@/protocol/ocppUriFormat';
import draft06 from 'ajv/dist/refs/json-schema-draft-06.json';

import authorizeReq from '@/../schemas/ocpp201/AuthorizeRequest.json';
import authorizeRes from '@/../schemas/ocpp201/AuthorizeResponse.json';
import bootReq from '@/../schemas/ocpp201/BootNotificationRequest.json';
import bootRes from '@/../schemas/ocpp201/BootNotificationResponse.json';
import changeAvailabilityReq from '@/../schemas/ocpp201/ChangeAvailabilityRequest.json';
import changeAvailabilityRes from '@/../schemas/ocpp201/ChangeAvailabilityResponse.json';
import clearCacheReq from '@/../schemas/ocpp201/ClearCacheRequest.json';
import clearCacheRes from '@/../schemas/ocpp201/ClearCacheResponse.json';
import dataTransferReq from '@/../schemas/ocpp201/DataTransferRequest.json';
import dataTransferRes from '@/../schemas/ocpp201/DataTransferResponse.json';
import getBaseReportReq from '@/../schemas/ocpp201/GetBaseReportRequest.json';
import getBaseReportRes from '@/../schemas/ocpp201/GetBaseReportResponse.json';
import getLocalListVersionReq from '@/../schemas/ocpp201/GetLocalListVersionRequest.json';
import getLocalListVersionRes from '@/../schemas/ocpp201/GetLocalListVersionResponse.json';
import getReportReq from '@/../schemas/ocpp201/GetReportRequest.json';
import getReportRes from '@/../schemas/ocpp201/GetReportResponse.json';
import setChargingProfileReq from '@/../schemas/ocpp201/SetChargingProfileRequest.json';
import setChargingProfileRes from '@/../schemas/ocpp201/SetChargingProfileResponse.json';
import clearChargingProfileReq from '@/../schemas/ocpp201/ClearChargingProfileRequest.json';
import clearChargingProfileRes from '@/../schemas/ocpp201/ClearChargingProfileResponse.json';
import getCompositeScheduleReq from '@/../schemas/ocpp201/GetCompositeScheduleRequest.json';
import getCompositeScheduleRes from '@/../schemas/ocpp201/GetCompositeScheduleResponse.json';
import getChargingProfilesReq from '@/../schemas/ocpp201/GetChargingProfilesRequest.json';
import getChargingProfilesRes from '@/../schemas/ocpp201/GetChargingProfilesResponse.json';
import reportChargingProfilesReq from '@/../schemas/ocpp201/ReportChargingProfilesRequest.json';
import reportChargingProfilesRes from '@/../schemas/ocpp201/ReportChargingProfilesResponse.json';
import notifyChargingLimitReq from '@/../schemas/ocpp201/NotifyChargingLimitRequest.json';
import notifyChargingLimitRes from '@/../schemas/ocpp201/NotifyChargingLimitResponse.json';
import notifyEVChargingNeedsReq from '@/../schemas/ocpp201/NotifyEVChargingNeedsRequest.json';
import notifyEVChargingNeedsRes from '@/../schemas/ocpp201/NotifyEVChargingNeedsResponse.json';
import notifyEVChargingScheduleReq from '@/../schemas/ocpp201/NotifyEVChargingScheduleRequest.json';
import notifyEVChargingScheduleRes from '@/../schemas/ocpp201/NotifyEVChargingScheduleResponse.json';
import clearedChargingLimitReq from '@/../schemas/ocpp201/ClearedChargingLimitRequest.json';
import clearedChargingLimitRes from '@/../schemas/ocpp201/ClearedChargingLimitResponse.json';
import setDisplayMessageReq from '@/../schemas/ocpp201/SetDisplayMessageRequest.json';
import setDisplayMessageRes from '@/../schemas/ocpp201/SetDisplayMessageResponse.json';
import clearDisplayMessageReq from '@/../schemas/ocpp201/ClearDisplayMessageRequest.json';
import clearDisplayMessageRes from '@/../schemas/ocpp201/ClearDisplayMessageResponse.json';
import getDisplayMessagesReq from '@/../schemas/ocpp201/GetDisplayMessagesRequest.json';
import getDisplayMessagesRes from '@/../schemas/ocpp201/GetDisplayMessagesResponse.json';
import notifyDisplayMessagesReq from '@/../schemas/ocpp201/NotifyDisplayMessagesRequest.json';
import notifyDisplayMessagesRes from '@/../schemas/ocpp201/NotifyDisplayMessagesResponse.json';
import getLogReq from '@/../schemas/ocpp201/GetLogRequest.json';
import getLogRes from '@/../schemas/ocpp201/GetLogResponse.json';
import logStatusNotificationReq from '@/../schemas/ocpp201/LogStatusNotificationRequest.json';
import logStatusNotificationRes from '@/../schemas/ocpp201/LogStatusNotificationResponse.json';
import notifyEventReq from '@/../schemas/ocpp201/NotifyEventRequest.json';
import notifyEventRes from '@/../schemas/ocpp201/NotifyEventResponse.json';
import updateFirmwareReq from '@/../schemas/ocpp201/UpdateFirmwareRequest.json';
import updateFirmwareRes from '@/../schemas/ocpp201/UpdateFirmwareResponse.json';
import firmwareStatusNotificationReq from '@/../schemas/ocpp201/FirmwareStatusNotificationRequest.json';
import firmwareStatusNotificationRes from '@/../schemas/ocpp201/FirmwareStatusNotificationResponse.json';
import publishFirmwareReq from '@/../schemas/ocpp201/PublishFirmwareRequest.json';
import publishFirmwareRes from '@/../schemas/ocpp201/PublishFirmwareResponse.json';
import publishFirmwareStatusNotificationReq from '@/../schemas/ocpp201/PublishFirmwareStatusNotificationRequest.json';
import publishFirmwareStatusNotificationRes from '@/../schemas/ocpp201/PublishFirmwareStatusNotificationResponse.json';
import unpublishFirmwareReq from '@/../schemas/ocpp201/UnpublishFirmwareRequest.json';
import unpublishFirmwareRes from '@/../schemas/ocpp201/UnpublishFirmwareResponse.json';
import getVariablesReq from '@/../schemas/ocpp201/GetVariablesRequest.json';
import getVariablesRes from '@/../schemas/ocpp201/GetVariablesResponse.json';
import setVariablesReq from '@/../schemas/ocpp201/SetVariablesRequest.json';
import setVariablesRes from '@/../schemas/ocpp201/SetVariablesResponse.json';
import reserveNowReq from '@/../schemas/ocpp201/ReserveNowRequest.json';
import reserveNowRes from '@/../schemas/ocpp201/ReserveNowResponse.json';
import cancelReservationReq from '@/../schemas/ocpp201/CancelReservationRequest.json';
import cancelReservationRes from '@/../schemas/ocpp201/CancelReservationResponse.json';
import reservationStatusUpdateReq from '@/../schemas/ocpp201/ReservationStatusUpdateRequest.json';
import reservationStatusUpdateRes from '@/../schemas/ocpp201/ReservationStatusUpdateResponse.json';
import setVariableMonitoringReq from '@/../schemas/ocpp201/SetVariableMonitoringRequest.json';
import setVariableMonitoringRes from '@/../schemas/ocpp201/SetVariableMonitoringResponse.json';
import clearVariableMonitoringReq from '@/../schemas/ocpp201/ClearVariableMonitoringRequest.json';
import clearVariableMonitoringRes from '@/../schemas/ocpp201/ClearVariableMonitoringResponse.json';
import getMonitoringReportReq from '@/../schemas/ocpp201/GetMonitoringReportRequest.json';
import getMonitoringReportRes from '@/../schemas/ocpp201/GetMonitoringReportResponse.json';
import setMonitoringBaseReq from '@/../schemas/ocpp201/SetMonitoringBaseRequest.json';
import setMonitoringBaseRes from '@/../schemas/ocpp201/SetMonitoringBaseResponse.json';
import setMonitoringLevelReq from '@/../schemas/ocpp201/SetMonitoringLevelRequest.json';
import setMonitoringLevelRes from '@/../schemas/ocpp201/SetMonitoringLevelResponse.json';
import notifyMonitoringReportReq from '@/../schemas/ocpp201/NotifyMonitoringReportRequest.json';
import notifyMonitoringReportRes from '@/../schemas/ocpp201/NotifyMonitoringReportResponse.json';
import costUpdatedReq from '@/../schemas/ocpp201/CostUpdatedRequest.json';
import costUpdatedRes from '@/../schemas/ocpp201/CostUpdatedResponse.json';
import setNetworkProfileReq from '@/../schemas/ocpp201/SetNetworkProfileRequest.json';
import setNetworkProfileRes from '@/../schemas/ocpp201/SetNetworkProfileResponse.json';
import customerInformationReq from '@/../schemas/ocpp201/CustomerInformationRequest.json';
import customerInformationRes from '@/../schemas/ocpp201/CustomerInformationResponse.json';
import notifyCustomerInformationReq from '@/../schemas/ocpp201/NotifyCustomerInformationRequest.json';
import notifyCustomerInformationRes from '@/../schemas/ocpp201/NotifyCustomerInformationResponse.json';
import certificateSignedReq from '@/../schemas/ocpp201/CertificateSignedRequest.json';
import certificateSignedRes from '@/../schemas/ocpp201/CertificateSignedResponse.json';
import installCertificateReq from '@/../schemas/ocpp201/InstallCertificateRequest.json';
import installCertificateRes from '@/../schemas/ocpp201/InstallCertificateResponse.json';
import deleteCertificateReq from '@/../schemas/ocpp201/DeleteCertificateRequest.json';
import deleteCertificateRes from '@/../schemas/ocpp201/DeleteCertificateResponse.json';
import getInstalledCertificateIdsReq from '@/../schemas/ocpp201/GetInstalledCertificateIdsRequest.json';
import getInstalledCertificateIdsRes from '@/../schemas/ocpp201/GetInstalledCertificateIdsResponse.json';
import signCertificateReq from '@/../schemas/ocpp201/SignCertificateRequest.json';
import signCertificateRes from '@/../schemas/ocpp201/SignCertificateResponse.json';
import securityEventNotificationReq from '@/../schemas/ocpp201/SecurityEventNotificationRequest.json';
import securityEventNotificationRes from '@/../schemas/ocpp201/SecurityEventNotificationResponse.json';
import get15118EVCertificateReq from '@/../schemas/ocpp201/Get15118EVCertificateRequest.json';
import get15118EVCertificateRes from '@/../schemas/ocpp201/Get15118EVCertificateResponse.json';
import getCertificateStatusReq from '@/../schemas/ocpp201/GetCertificateStatusRequest.json';
import getCertificateStatusRes from '@/../schemas/ocpp201/GetCertificateStatusResponse.json';
import getTransactionStatusReq from '@/../schemas/ocpp201/GetTransactionStatusRequest.json';
import getTransactionStatusRes from '@/../schemas/ocpp201/GetTransactionStatusResponse.json';
import heartbeatReq from '@/../schemas/ocpp201/HeartbeatRequest.json';
import heartbeatRes from '@/../schemas/ocpp201/HeartbeatResponse.json';
import sendLocalListReq from '@/../schemas/ocpp201/SendLocalListRequest.json';
import sendLocalListRes from '@/../schemas/ocpp201/SendLocalListResponse.json';
import triggerMessageReq from '@/../schemas/ocpp201/TriggerMessageRequest.json';
import triggerMessageRes from '@/../schemas/ocpp201/TriggerMessageResponse.json';
import unlockConnectorReq from '@/../schemas/ocpp201/UnlockConnectorRequest.json';
import unlockConnectorRes from '@/../schemas/ocpp201/UnlockConnectorResponse.json';
import notifyReportReq from '@/../schemas/ocpp201/NotifyReportRequest.json';
import notifyReportRes from '@/../schemas/ocpp201/NotifyReportResponse.json';
import meterValuesReq from '@/../schemas/ocpp201/MeterValuesRequest.json';
import meterValuesRes from '@/../schemas/ocpp201/MeterValuesResponse.json';
import requestStartReq from '@/../schemas/ocpp201/RequestStartTransactionRequest.json';
import requestStartRes from '@/../schemas/ocpp201/RequestStartTransactionResponse.json';
import requestStopReq from '@/../schemas/ocpp201/RequestStopTransactionRequest.json';
import requestStopRes from '@/../schemas/ocpp201/RequestStopTransactionResponse.json';
import resetReq from '@/../schemas/ocpp201/ResetRequest.json';
import resetRes from '@/../schemas/ocpp201/ResetResponse.json';
import statusReq from '@/../schemas/ocpp201/StatusNotificationRequest.json';
import statusRes from '@/../schemas/ocpp201/StatusNotificationResponse.json';
import transactionEventReq from '@/../schemas/ocpp201/TransactionEventRequest.json';
import transactionEventRes from '@/../schemas/ocpp201/TransactionEventResponse.json';

export type Direction = 'request' | 'response';
export type Validator = (data: unknown) => { valid: boolean; errors: readonly string[] };

const ajv = new Ajv({ allErrors: true, strict: false });
ajv.addMetaSchema(draft06);
addFormats(ajv);
relaxOcppUriFormats(ajv);

const SCHEMAS: Record<string, { request: unknown; response: unknown }> = {
  Authorize: { request: authorizeReq, response: authorizeRes },
  BootNotification: { request: bootReq, response: bootRes },
  // Session 19a — CSMS-initiated parity actions + outbound DataTransfer.
  ChangeAvailability: { request: changeAvailabilityReq, response: changeAvailabilityRes },
  ClearCache: { request: clearCacheReq, response: clearCacheRes },
  DataTransfer: { request: dataTransferReq, response: dataTransferRes },
  GetBaseReport: { request: getBaseReportReq, response: getBaseReportRes },
  GetLocalListVersion: { request: getLocalListVersionReq, response: getLocalListVersionRes },
  GetReport: { request: getReportReq, response: getReportRes },
  GetTransactionStatus: { request: getTransactionStatusReq, response: getTransactionStatusRes },
  Heartbeat: { request: heartbeatReq, response: heartbeatRes },
  MeterValues: { request: meterValuesReq, response: meterValuesRes },
  NotifyReport: { request: notifyReportReq, response: notifyReportRes },
  RequestStartTransaction: { request: requestStartReq, response: requestStartRes },
  RequestStopTransaction: { request: requestStopReq, response: requestStopRes },
  // Session 19c — Smart Charging (inbound + outbound).
  SetChargingProfile: { request: setChargingProfileReq, response: setChargingProfileRes },
  ClearChargingProfile: { request: clearChargingProfileReq, response: clearChargingProfileRes },
  GetCompositeSchedule: { request: getCompositeScheduleReq, response: getCompositeScheduleRes },
  GetChargingProfiles: { request: getChargingProfilesReq, response: getChargingProfilesRes },
  ReportChargingProfiles: { request: reportChargingProfilesReq, response: reportChargingProfilesRes },
  NotifyChargingLimit: { request: notifyChargingLimitReq, response: notifyChargingLimitRes },
  ClearedChargingLimit: { request: clearedChargingLimitReq, response: clearedChargingLimitRes },
  // CP→CSMS V2G / smart-charging negotiation (ISO 15118-adjacent). Outbound
  // capability surface — the simulator has no EV-side negotiation to trigger
  // them automatically (like NotifyChargingLimit).
  NotifyEVChargingNeeds: { request: notifyEVChargingNeedsReq, response: notifyEVChargingNeedsRes },
  NotifyEVChargingSchedule: {
    request: notifyEVChargingScheduleReq,
    response: notifyEVChargingScheduleRes,
  },
  // Session 19d — Display Messages.
  SetDisplayMessage: { request: setDisplayMessageReq, response: setDisplayMessageRes },
  ClearDisplayMessage: { request: clearDisplayMessageReq, response: clearDisplayMessageRes },
  GetDisplayMessages: { request: getDisplayMessagesReq, response: getDisplayMessagesRes },
  NotifyDisplayMessages: { request: notifyDisplayMessagesReq, response: notifyDisplayMessagesRes },
  // Session 19e — diagnostics.
  GetLog: { request: getLogReq, response: getLogRes },
  LogStatusNotification: { request: logStatusNotificationReq, response: logStatusNotificationRes },
  NotifyEvent: { request: notifyEventReq, response: notifyEventRes },
  // Session 20 — firmware management.
  UpdateFirmware: { request: updateFirmwareReq, response: updateFirmwareRes },
  FirmwareStatusNotification: {
    request: firmwareStatusNotificationReq,
    response: firmwareStatusNotificationRes,
  },
  // Local Controller role — CSMS publishes a firmware image to the LC, which
  // caches it for downstream charging stations. Inbound request + the LC's
  // outbound PublishFirmwareStatusNotification progress.
  PublishFirmware: { request: publishFirmwareReq, response: publishFirmwareRes },
  PublishFirmwareStatusNotification: {
    request: publishFirmwareStatusNotificationReq,
    response: publishFirmwareStatusNotificationRes,
  },
  UnpublishFirmware: { request: unpublishFirmwareReq, response: unpublishFirmwareRes },
  // Device Model variable get/set — dispatched + handled since Session 19b but
  // previously bypassed inbound Ajv validation (not in this registry). Register
  // so malformed getVariableData/setVariableData payloads are rejected.
  GetVariables: { request: getVariablesReq, response: getVariablesRes },
  SetVariables: { request: setVariablesReq, response: setVariablesRes },
  // Session 21 — reservation.
  ReserveNow: { request: reserveNowReq, response: reserveNowRes },
  CancelReservation: { request: cancelReservationReq, response: cancelReservationRes },
  ReservationStatusUpdate: {
    request: reservationStatusUpdateReq,
    response: reservationStatusUpdateRes,
  },
  // Session 22 — variable monitoring.
  SetVariableMonitoring: { request: setVariableMonitoringReq, response: setVariableMonitoringRes },
  ClearVariableMonitoring: {
    request: clearVariableMonitoringReq,
    response: clearVariableMonitoringRes,
  },
  GetMonitoringReport: { request: getMonitoringReportReq, response: getMonitoringReportRes },
  SetMonitoringBase: { request: setMonitoringBaseReq, response: setMonitoringBaseRes },
  SetMonitoringLevel: { request: setMonitoringLevelReq, response: setMonitoringLevelRes },
  NotifyMonitoringReport: {
    request: notifyMonitoringReportReq,
    response: notifyMonitoringReportRes,
  },
  // Session 23 — tariff / cost.
  CostUpdated: { request: costUpdatedReq, response: costUpdatedRes },
  // Session 24 — network profile + customer information.
  SetNetworkProfile: { request: setNetworkProfileReq, response: setNetworkProfileRes },
  CustomerInformation: { request: customerInformationReq, response: customerInformationRes },
  NotifyCustomerInformation: {
    request: notifyCustomerInformationReq,
    response: notifyCustomerInformationRes,
  },
  // Session 25 — security / certificates.
  CertificateSigned: { request: certificateSignedReq, response: certificateSignedRes },
  InstallCertificate: { request: installCertificateReq, response: installCertificateRes },
  DeleteCertificate: { request: deleteCertificateReq, response: deleteCertificateRes },
  GetInstalledCertificateIds: {
    request: getInstalledCertificateIdsReq,
    response: getInstalledCertificateIdsRes,
  },
  SignCertificate: { request: signCertificateReq, response: signCertificateRes },
  SecurityEventNotification: {
    request: securityEventNotificationReq,
    response: securityEventNotificationRes,
  },
  // Session 26 — ISO 15118 Plug & Charge.
  Get15118EVCertificate: { request: get15118EVCertificateReq, response: get15118EVCertificateRes },
  GetCertificateStatus: { request: getCertificateStatusReq, response: getCertificateStatusRes },
  Reset: { request: resetReq, response: resetRes },
  SendLocalList: { request: sendLocalListReq, response: sendLocalListRes },
  StatusNotification: { request: statusReq, response: statusRes },
  TriggerMessage: { request: triggerMessageReq, response: triggerMessageRes },
  TransactionEvent: { request: transactionEventReq, response: transactionEventRes },
  UnlockConnector: { request: unlockConnectorReq, response: unlockConnectorRes },
};

const VALIDATORS = new Map<string, Validator>();

function keyFor(action: string, direction: Direction): string {
  return `${action}|${direction}`;
}

function compile(action: string, direction: Direction): Validator {
  const schemaSet = SCHEMAS[action];
  if (!schemaSet) {
    return () => ({ valid: false, errors: [`unknown action "${action}"`] });
  }
  const schema = schemaSet[direction];
  const compiled = ajv.compile(schema as Record<string, unknown>);
  return (data) => {
    const ok = compiled(data);
    if (ok) return { valid: true, errors: [] };
    const errors = (compiled.errors ?? []).map(
      (e) => `${e.instancePath || '<root>'} ${e.message ?? 'unknown'}`,
    );
    return { valid: false, errors };
  };
}

export function validatorFor(action: string, direction: Direction): Validator {
  const k = keyFor(action, direction);
  let v = VALIDATORS.get(k);
  if (!v) {
    v = compile(action, direction);
    VALIDATORS.set(k, v);
  }
  return v;
}

export function listActions(): readonly string[] {
  return Object.keys(SCHEMAS);
}
